import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, FileText, Video, Upload, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = {
  title: '',
  description: '',
  province: 'Sud-Kivu',
  image_url: '',
  document_url: '',
  video_url: '',
  project_number: '',
  execution_location: '',
  duration: '',
  target_population: '',
  funder_name: '',
  start_date: ''
};

export default function ProjectsManager() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pdfInputRef = useRef(null);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        target_population: formData.target_population ? parseInt(formData.target_population, 10) : null
      };
      if (editingId) {
        await axios.put(`${API}/projects/${editingId}`, payload, { withCredentials: true });
        toast.success('Projet mis à jour');
      } else {
        await axios.post(`${API}/projects`, payload, { withCredentials: true });
        toast.success('Projet créé');
      }
      resetForm();
      fetchProjects();
    } catch (error) { toast.error('Erreur lors de la sauvegarde'); }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 10 Mo');
      return;
    }
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/files/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, document_url: res.data.url });
      toast.success('PDF chargé');
    } catch (error) { toast.error('Erreur lors du chargement'); }
    finally { setUploadingPdf(false); if (pdfInputRef.current) pdfInputRef.current.value = ''; }
  };

  const handleEdit = (project) => {
    setEditingId(project.project_id);
    setFormData({
      title: project.title,
      description: project.description,
      province: project.province,
      image_url: project.image_url || '',
      document_url: project.document_url || '',
      video_url: project.video_url || '',
      project_number: project.project_number || '',
      execution_location: project.execution_location || '',
      duration: project.duration || '',
      target_population: project.target_population || '',
      funder_name: project.funder_name || '',
      start_date: project.start_date ? project.start_date.split('T')[0] : ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) return;
    try {
      await axios.delete(`${API}/projects/${id}`, { withCredentials: true });
      toast.success('Projet supprimé');
      fetchProjects();
    } catch (error) { toast.error('Erreur lors de la suppression'); }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div data-testid="projects-manager">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Gestion des Projets ({projects.length})
        </h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary inline-flex items-center" data-testid="add-project-button">
          <Plus size={20} className="mr-2" />
          {showForm ? 'Annuler' : 'Ajouter'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card mb-6" data-testid="project-form">
          <div className="space-y-4">
            <input type="text" placeholder="Titre" required value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="project-title-input" />
            <textarea placeholder="Description" required rows={4} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="project-description-input" />
            <select value={formData.province} onChange={(e) => setFormData({...formData, province: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="project-province-select">
              <option value="Sud-Kivu">Sud-Kivu</option>
              <option value="Haut-Katanga">Haut-Katanga</option>
              <option value="Kinshasa">Kinshasa</option>
              <option value="Kongo Central">Kongo Central</option>
            </select>
            <input type="url" placeholder="URL de l'image (optionnel)" value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="project-image-input" />

            {/* Extended project fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-[#F9F8F6] rounded-lg">
              <div>
                <label className="text-xs font-bold text-[#2E332D] mb-1 block">N° du projet</label>
                <input type="text" placeholder="Ex: P-2026-001" value={formData.project_number} onChange={(e) => setFormData({...formData, project_number: e.target.value})} className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" data-testid="project-number-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#2E332D] mb-1 block">Lieu d&apos;exécution</label>
                <input type="text" placeholder="Ex: Bushumba, Kabare" value={formData.execution_location} onChange={(e) => setFormData({...formData, execution_location: e.target.value})} className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" data-testid="project-location-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#2E332D] mb-1 block">Durée du projet</label>
                <input type="text" placeholder="Ex: 24 mois" value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" data-testid="project-duration-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#2E332D] mb-1 block">Population cible</label>
                <input type="number" min="0" placeholder="Ex: 1500" value={formData.target_population} onChange={(e) => setFormData({...formData, target_population: e.target.value})} className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" data-testid="project-target-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#2E332D] mb-1 block">Bailleur de fonds</label>
                <input type="text" placeholder="Ex: WBI / SSS" value={formData.funder_name} onChange={(e) => setFormData({...formData, funder_name: e.target.value})} className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" data-testid="project-funder-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#2E332D] mb-1 block">Date de création / lancement</label>
                <input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} className="w-full px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm" data-testid="project-start-input" />
              </div>
            </div>

            {/* PDF Upload */}
            <div className="border-2 border-dashed border-[#E5E3DB] rounded-lg p-4">
              <label className="flex items-center gap-2 text-sm font-bold text-[#2E332D] mb-2">
                <FileText size={16} /> Document PDF (max 10 Mo)
              </label>
              <input ref={pdfInputRef} type="file" accept="application/pdf" onChange={handlePdfUpload} className="text-sm" disabled={uploadingPdf} data-testid="project-pdf-upload" />
              {uploadingPdf && <p className="text-xs text-[#5C665A] mt-1">Chargement...</p>}
              {formData.document_url && (
                <div className="mt-2 flex items-center justify-between bg-[#F9F8F6] p-2 rounded">
                  <a href={`${BACKEND_URL}${formData.document_url}`} target="_blank" rel="noreferrer" className="text-sm text-[#4A6B48] hover:underline inline-flex items-center gap-1">
                    <ExternalLink size={12} /> PDF chargé
                  </a>
                  <button type="button" onClick={() => setFormData({...formData, document_url: ''})} className="text-xs text-red-500 hover:underline">Retirer</button>
                </div>
              )}
            </div>

            {/* Video URL */}
            <div className="border-2 border-dashed border-[#E5E3DB] rounded-lg p-4">
              <label className="flex items-center gap-2 text-sm font-bold text-[#2E332D] mb-2">
                <Video size={16} /> Lien vidéo (YouTube, Vimeo, etc.)
              </label>
              <input type="url" placeholder="https://www.youtube.com/watch?v=..." value={formData.video_url} onChange={(e) => setFormData({...formData, video_url: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg text-sm" data-testid="project-video-input" />
            </div>

            <div className="flex gap-2">
              <button type="submit" className="btn-primary" data-testid="submit-project-button">
                {editingId ? 'Mettre à jour' : 'Créer'}
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">Annuler</button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {projects.length === 0 ? (
            <p className="text-center text-[#5C665A] py-8">Aucun projet. Cliquez sur &quot;Ajouter&quot; pour créer le premier.</p>
          ) : projects.map((project) => (
            <div key={project.project_id} className="card" data-testid={`project-item-${project.project_id}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="text-xs font-medium text-[#D87D4A] px-2 py-1 bg-[#D87D4A]/10 rounded">
                      {project.province}
                    </span>
                    {project.document_url && (
                      <span className="text-xs text-blue-700 px-2 py-1 bg-blue-100 rounded inline-flex items-center gap-1">
                        <FileText size={12} /> PDF
                      </span>
                    )}
                    {project.video_url && (
                      <span className="text-xs text-red-700 px-2 py-1 bg-red-100 rounded inline-flex items-center gap-1">
                        <Video size={12} /> Vidéo
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-[#2E332D]">{project.title}</h3>
                  <p className="text-sm text-[#5C665A] mt-1">{project.description}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => handleEdit(project)} className="p-2 hover:bg-gray-100 rounded" data-testid={`edit-project-${project.project_id}`}>
                    <Edit2 size={18} className="text-[#4A6B48]" />
                  </button>
                  <button onClick={() => handleDelete(project.project_id)} className="p-2 hover:bg-gray-100 rounded" data-testid={`delete-project-${project.project_id}`}>
                    <Trash2 size={18} className="text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
